// dynamicFieldMarkup.ts

export type DynamicFieldType = 'text' | 'dropdown' | 'number';

export interface DynamicFieldMeta {
  fieldType?: DynamicFieldType;
  /** 'dropdown' only — the clinic's own option list for this field. */
  options?: string[];
  /** 'number' only — unit suffix, e.g. "cm", "%". Never registered in CHAIN_FIELDS. */
  unit?: string;
  /** Any type — the exact raw SR concept name (CodeMeaning) this field should catch. */
  srConcept?: string;
}

/**
 * Give a user-created dynamic field its identity when it's first inserted.
 *
 * Mirrors `chainFieldMarkup.ts`'s `wrapChainField`, minus the %-unit boundary
 * handling — that exists only because CHAIN_FIELDS carries units, and dynamic
 * fields never enter CHAIN_FIELDS. 'text' (the v1 default) is always plain text
 * with just a key and a label. 'dropdown'/'number' (18 Sep) carry their own
 * type-specific data as extra attributes, read back by dynamicFieldSync.ts.
 */
export function wrapDynamicField(
  key: string,
  label: string,
  value: string,
  meta: DynamicFieldMeta = {}
): string {
  const { fieldType = 'text', options, unit, srConcept } = meta;
  const text = value && value.trim() ? value : `[${label}]`;

  const attrs = [`class="${key}"`, `title="${escapeAttr(label)}"`];
  if (fieldType !== 'text') {
    attrs.push(`data-field-type="${fieldType}"`);
  }
  if (fieldType === 'dropdown' && options && options.length > 0) {
    attrs.push(`data-options="${escapeAttr(JSON.stringify(options))}"`);
  }
  if (fieldType === 'number' && unit) {
    attrs.push(`data-unit="${escapeAttr(unit)}"`);
  }
  if (srConcept && srConcept.trim()) {
    attrs.push(`data-sr-concept="${escapeAttr(srConcept.trim())}"`);
  }

  return `<span data-dynamic-field ${attrs.join(' ')}>${escapeText(text)}</span>`;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
